<template>
  <NativeDrawer v-model="drawer" :before-close="handleDrawerClose" size="80%">
    <template #header>
      <h4>新闻动态信息</h4>
    </template>
    <template #default>
      <div class="news-content">
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-container">
          <NativeSkeleton :rows="5" animated />
        </div>

        <!-- 新闻列表 -->
        <div v-else class="news-list">
          <!-- 搜索筛选组件 -->
          <BaseSearch
            :search-form="searchForm"
            :fields="searchFields"
            @search="handleSearch"
            @reset="handleReset"
          />

          <!-- 表格组件 -->
          <NewsTable
            :news-list="newsList"
            @edit="handleEdit"
            @delete="handleDeleteConfirm"
          />

          <!-- 分页组件 -->
          <BasePagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :total="total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>

        <!-- 编辑表单组件 -->
        <NewsForm
          v-model:visible="editDialogVisible"
          :is-edit="isEdit"
          :form-data="formData"
          :submit-loading="submitLoading"
          @submit="handleSubmitForm"
          @cancel="handleFormCancel"
        />
      </div>
    </template>
    <template #footer>
      <div
        style="flex: auto; display: flex; justify-content: flex-end; gap: 10px"
      >
        <NativeButton @click="addClick" type="success">新增新闻</NativeButton>
        <NativeButton type="primary" @click="confirmClick">关闭</NativeButton>
      </div>
    </template>
  </NativeDrawer>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from "vue";

import type { SearchField } from "@/components/NativeUI/types";
import { useNewsStore, type NewsType } from "@/stores/news";
import { notify } from "@/utils/nativeFeedback";

const newsStore = useNewsStore();
// 导入子组件
import BaseSearch from "./BaseSearch/index.vue";
import NewsTable from "./news/NewsTable.vue";
import BasePagination from "./BasePagination/index.vue";
import NewsForm from "./news/NewsForm.vue";

// ==================== Props & Emits ====================
const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
}>();

// ==================== Drawer 状态 ====================
const drawer = ref(props.visible);

watch(
  () => props.visible,
  (newVal) => {
    drawer.value = newVal;
    if (newVal) {
      loadNewsList();
    }
  },
);

watch(drawer, (newVal) => {
  emit("update:visible", newVal);
});

// ==================== 数据相关 ====================
const loading = ref(false);
const allNewsList = ref<NewsType[]>([]);
const newsList = ref<NewsType[]>([]);
const currentPage = ref(1);
const pageSize = ref(10);
const total = ref(0);

// 搜索表单
const searchForm = ref({
  title: "",
  category: "",
  status: null as number | null,
});

// 分类选项
const categoryOptions = computed(() => {
  const categories = new Set(
    allNewsList.value.map((item) => item.category).filter(Boolean),
  );
  return Array.from(categories);
});

// 状态选项
const statusOptions = [
  { label: "已下架", value: 0 },
  { label: "已发布", value: 1 },
];

// 搜索字段配置
const searchFields = computed<SearchField[]>(() => [
  {
    label: "标题",
    prop: "title",
    type: "input",
    placeholder: "请输入标题",
    width: "200px",
  },
  {
    label: "分类",
    prop: "category",
    type: "select",
    placeholder: "请选择分类",
    width: "150px",
    options: categoryOptions.value.map((item) => ({
      label: item,
      value: item,
    })),
  },
  {
    label: "状态",
    prop: "status",
    type: "select",
    placeholder: "请选择状态",
    width: "120px",
    options: statusOptions,
  },
]);

// 筛选后的数据
const filteredList = computed(() => {
  let list = allNewsList.value;

  if (searchForm.value.title) {
    list = list.filter((item) => item.title?.includes(searchForm.value.title));
  }

  if (searchForm.value.category) {
    list = list.filter((item) => item.category === searchForm.value.category);
  }

  if (
    searchForm.value.status !== null &&
    searchForm.value.status !== undefined
  ) {
    list = list.filter((item) => item.status === searchForm.value.status);
  }

  return list;
});

// 编辑表单
const editDialogVisible = ref(false);
const isEdit = ref(false);
const submitLoading = ref(false);
const formData = ref<Partial<NewsType>>({
  id: 0,
  title: "",
  category: "",
  date: "",
  author: "",
  excerpt: "",
  content: "",
  image: "",
  status: 0,
  views: 0,
});

// ==================== API 调用 ====================
const updatePageData = () => {
  const list = filteredList.value;
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  newsList.value = list.slice(start, end);
  total.value = list.length;
};

const loadNewsList = async () => {
  loading.value = true;
  try {
    await newsStore.refreshNews();
    allNewsList.value = newsStore.getNewsList;
    updatePageData();
  } catch (error) {
    console.error("加载新闻列表失败:", error);
    notify("加载新闻列表失败", "error");
  } finally {
    loading.value = false;
  }
};

// ==================== 事件处理 ====================
const handleSearch = () => {
  currentPage.value = 1;
  updatePageData();
};

const handleReset = () => {
  searchForm.value = {
    title: "",
    category: "",
    status: null,
  };
  currentPage.value = 1;
  updatePageData();
};

const handleSizeChange = (val: number) => {
  pageSize.value = val;
  currentPage.value = 1;
  updatePageData();
};

const handleCurrentChange = (val: number) => {
  currentPage.value = val;
  updatePageData();
};

const addClick = () => {
  isEdit.value = false;
  formData.value = {
    id: 0,
    title: "",
    category: "",
    date: new Date().toISOString().split("T")[0],
    author: "",
    excerpt: "",
    content: "",
    image: "",
    status: 0,
    views: 0,
  };
  editDialogVisible.value = true;
};

const handleEdit = (row: NewsType) => {
  isEdit.value = true;
  formData.value = {
    id: row.id,
    title: row.title || "",
    category: row.category || "",
    date: row.date || "",
    author: row.author || "",
    excerpt: row.excerpt || "",
    content: row.content || "",
    image: row.image || "",
    status: row.status ?? 0,
    views: row.views || 0,
  };
  editDialogVisible.value = true;
};

const handleDeleteConfirm = async (row: NewsType) => {
  if (!window.confirm(`确认删除新闻 "${row.title}" 吗?`)) return;
  if (await newsStore.deleteNews(row.id)) {
    allNewsList.value = newsStore.getNewsList;
    updatePageData();
  }
};

const handleSubmitForm = async (data: {
  formData: Partial<NewsType>;
  originalData: Partial<NewsType>;
}) => {
  submitLoading.value = true;
  try {
    const success =
      isEdit.value && data.formData.id
        ? await newsStore.updateNewsById(data.formData.id, data.formData)
        : await newsStore.createNews(data.formData);

    if (success) {
      allNewsList.value = newsStore.getNewsList;
      updatePageData();
      editDialogVisible.value = false;
    }
  } finally {
    submitLoading.value = false;
  }
};

const handleFormCancel = () => {
  formData.value = {
    id: 0,
    title: "",
    category: "",
    date: "",
    author: "",
    excerpt: "",
    content: "",
    image: "",
    status: 0,
    views: 0,
  };
  isEdit.value = false;
};

const handleDrawerClose = (done: () => void) => {
  if (window.confirm("确认是否关闭?")) done();
};

const confirmClick = () => {
  drawer.value = false;
};

// 监听筛选结果变化
watch(filteredList, () => {
  currentPage.value = 1;
  updatePageData();
});
</script>

<style scoped lang="scss">
.news-content {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .loading-container {
    padding: 20px;
  }

  .news-list {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    // 搜索筛选区域 - 固定在顶部
    :deep(.search-filter) {
      flex-shrink: 0;
    }

    // 表格容器 - 占据剩余空间并可滚动
    :deep(.news-table) {
      flex: 1;
      min-height: 0;
      overflow: auto;
    }

    // 分页组件 - 固定在底部
    :deep(.pagination-container) {
      flex-shrink: 0;
      border-top: 1px solid #ebeef5;
      background-color: #fff;
    }
  }
}
</style>
